/**
 * AdminNouveauxEntrantsView
 * ─────────────────────────
 * Tableau de bord admin — rubrique "Nouveaux entrants"
 *
 * Affiche :
 *   - 4 KPI : total invitations | acceptés | en attente | refusés
 *   - Taux d'acceptation + délai moyen de réponse
 *   - Tableau filtrable par statut avec la liste détaillée des bénéficiaires
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Users, CheckCircle2, Clock, XCircle,
  RefreshCw, TrendingUp, Timer, Search, Filter, ShieldCheck
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return iso; }
};

// ── Badge statut ──────────────────────────────────────────────────────────────
const StatutBadge = ({ statut }) => {
  const map = {
    accepte:      { label: 'Accepté',      cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    en_attente:   { label: 'En attente',   cls: 'bg-amber-100 text-amber-800 border-amber-200'       },
    refuse:       { label: 'Refusé',       cls: 'bg-red-100 text-red-800 border-red-200'             },
    non_envoye:   { label: 'Non invité',   cls: 'bg-slate-100 text-slate-600 border-slate-200'       },
    revoque:      { label: 'Révoqué',      cls: 'bg-slate-100 text-slate-500 border-slate-200'       },
  };
  const c = map[statut] || map.non_envoye;
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${c.cls}`}>
      {c.label}
    </span>
  );
};

// ── Carte KPI ─────────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, icon: Icon, colorBg, colorText, colorIcon, sub }) => (
  <Card className={`${colorBg} rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300`}>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <CardTitle className={`text-xs font-bold uppercase tracking-wider ${colorText}`}>{title}</CardTitle>
        <Icon className={`w-8 h-8 ${colorIcon}`} />
      </div>
    </CardHeader>
    <CardContent>
      <div className={`text-4xl font-extrabold ${colorText}`}>{value}</div>
      {sub && <p className={`text-xs mt-1 ${colorText} opacity-75`}>{sub}</p>}
    </CardContent>
  </Card>
);

// ── Composant principal ───────────────────────────────────────────────────────
export default function AdminNouveauxEntrantsView() {
  const [stats, setStats]         = useState(null);
  const [details, setDetails]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtre, setFiltre]       = useState('tous');   // tous | accepte | en_attente | refuse | non_envoye
  const [search, setSearch]       = useState('');
  const [validating, setValidating] = useState(null);  // id du bénéficiaire en cours de validation
  const autoRefreshRef            = useRef(null);

  // ── Chargement des données ────────────────────────────────────────────────
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const [rStats, rDetails] = await Promise.all([
        fetch(`${API_BASE}/stats/entrees-dispositif`,         { headers: authHeaders() }),
        fetch(`${API_BASE}/stats/entrees-dispositif/details`, { headers: authHeaders() }),
      ]);
      if (rStats.ok)   setStats(await rStats.json());
      if (rDetails.ok) setDetails(await rDetails.json());
    } catch (e) {
      console.error('[AdminNouveauxEntrantsView] loadData:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh toutes les 30 secondes
    autoRefreshRef.current = setInterval(() => loadData(true), 30000);
    return () => clearInterval(autoRefreshRef.current);
  }, [loadData]);

  // ── Validation admin ──────────────────────────────────────────────────────
  const handleValiderAdmin = async (apprenantId, apprenantNom) => {
    if (!window.confirm(`Valider manuellement l'entrée de ${apprenantNom} dans le dispositif ?`)) return;
    setValidating(apprenantId);
    try {
      const r = await fetch(`${API_BASE}/entree-dispositif/valider-admin/${apprenantId}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await r.json();
      if (r.ok) {
        await loadData(true);
      } else {
        alert(data.detail || 'Erreur lors de la validation.');
      }
    } catch {
      alert('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      setValidating(null);
    }
  };

  // ── Filtrage ──────────────────────────────────────────────────────────────
  // Le backend retourne le champ "invitation_statut"
  const statutDe = (b) => b.invitation_statut || 'non_envoye';

  const detailsFiltres = details.filter(b => {
    const statutMatch = filtre === 'tous' || statutDe(b) === filtre;
    const searchMatch = search.trim() === '' ||
      `${b.prenom} ${b.nom}`.toLowerCase().includes(search.toLowerCase()) ||
      (b.zone_geographique || '').toLowerCase().includes(search.toLowerCase());
    return statutMatch && searchMatch;
  });

  // ── Filtres rapides ───────────────────────────────────────────────────────
  const filtres = [
    { key: 'tous',       label: 'Tous',       count: details.length },
    { key: 'accepte',    label: 'Acceptés',   count: stats?.par_statut?.accepte    || 0 },
    { key: 'en_attente', label: 'En attente', count: stats?.par_statut?.en_attente || 0 },
    { key: 'refuse',     label: 'Refusés',    count: stats?.par_statut?.refuse     || 0 },
    { key: 'non_envoye', label: 'Non invités', count: details.filter(b => statutDe(b) === 'non_envoye').length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Nouveaux entrants
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">Suivi des consentements d'entrée dans le dispositif</p>
        </div>
        <Button
          onClick={() => loadData(true)}
          disabled={refreshing}
          variant="outline"
          className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* ── KPI ──────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Total invitations"
          value={stats?.total_invitations || 0}
          icon={Users}
          colorBg="bg-blue-50 border-blue-100"
          colorText="text-blue-900"
          colorIcon="text-blue-500"
        />
        <KpiCard
          title="Acceptés"
          value={stats?.par_statut?.accepte || 0}
          icon={CheckCircle2}
          colorBg="bg-emerald-50 border-emerald-100"
          colorText="text-emerald-900"
          colorIcon="text-emerald-500"
          sub={`Taux : ${stats?.taux_acceptation || 0}%`}
        />
        <KpiCard
          title="En attente"
          value={stats?.par_statut?.en_attente || 0}
          icon={Clock}
          colorBg="bg-amber-50 border-amber-100"
          colorText="text-amber-900"
          colorIcon="text-amber-500"
        />
        <KpiCard
          title="Refusés"
          value={stats?.par_statut?.refuse || 0}
          icon={XCircle}
          colorBg="bg-red-50 border-red-100"
          colorText="text-red-900"
          colorIcon="text-red-400"
          sub={`Taux : ${stats?.taux_refus || 0}%`}
        />
      </div>

      {/* ── Métriques secondaires ─────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-white border-slate-200 rounded-2xl shadow-sm">
            <CardContent className="py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Taux d'acceptation</p>
                <p className="text-2xl font-extrabold text-slate-900">{stats.taux_acceptation}%</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 rounded-2xl shadow-sm">
            <CardContent className="py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                <Timer className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Délai moyen de réponse</p>
                <p className="text-2xl font-extrabold text-slate-900">
                  {stats.delai_moyen_reponse_heures > 0
                    ? `${stats.delai_moyen_reponse_heures}h`
                    : '—'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Tableau détaillé ──────────────────────────────────────────────── */}
      <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Liste des bénéficiaires
            </CardTitle>
            {/* Barre de recherche */}
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </div>

          {/* Filtres rapides */}
          <div className="flex flex-wrap gap-2 mt-3">
            <Filter className="w-4 h-4 text-slate-400 self-center" />
            {filtres.map(f => (
              <button
                key={f.key}
                onClick={() => setFiltre(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors
                  ${filtre === f.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              >
                {f.label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                  ${filtre === f.key ? 'bg-blue-500' : 'bg-slate-100 text-slate-500'}`}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {detailsFiltres.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun bénéficiaire trouvé pour ce filtre.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Bénéficiaire</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Zone</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Référent</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Date réponse</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {detailsFiltres.map((b, i) => (
                    <tr
                      key={b.id || i}
                      className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-3 px-3">
                        <div className="font-semibold text-slate-900">
                          {b.prenom} {b.nom}
                        </div>
                        {b.commentaire_refus && (
                          <div className="text-xs text-red-500 mt-0.5 truncate max-w-[200px]" title={b.commentaire_refus}>
                            {b.commentaire_refus}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-slate-500 hidden md:table-cell">
                        {b.zone_geographique || '—'}
                      </td>
                      <td className="py-3 px-3 text-slate-500 hidden lg:table-cell">
                        {b.referent_id ? <span className="text-xs text-slate-400">—</span> : '—'}
                      </td>
                      <td className="py-3 px-3">
                        <StatutBadge statut={statutDe(b)} />
                      </td>
                      <td className="py-3 px-3 text-slate-500 hidden sm:table-cell">
                        {statutDe(b) === 'accepte'
                          ? fmt(b.date_entree_dispositif || b.date_reponse)
                          : b.date_invitation
                            ? <span className="text-amber-600">{fmt(b.date_invitation)}</span>
                            : '—'}
                      </td>
                      <td className="py-3 px-3">
                        {statutDe(b) !== 'accepte' && (
                          <button
                            onClick={() => handleValiderAdmin(b.id, `${b.prenom} ${b.nom}`)}
                            disabled={validating === b.id}
                            title="Valider manuellement l'entrée dans le dispositif"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold
                              bg-emerald-50 text-emerald-700 border border-emerald-200
                              hover:bg-emerald-100 hover:border-emerald-300 transition-colors
                              disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {validating === b.id
                              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              : <ShieldCheck className="w-3.5 h-3.5" />}
                            Valider
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
